import { memo, useCallback } from 'react';
import { CheckPicker, Col, FlexboxGrid, FormGroup, Icon, IconButton, Nav, Popover, Row, SelectPicker } from 'rsuite';
import {
  FilterData,
  FilterPickerProps,
  InputFilterProps,
  CustomNavProps,
  FilterItem,
  FilterConfig,
  FilterType,
  MoreFilterDropdownProps,
  SelectedFilter,
} from './types';

const STYLES = {
  marginBottom: { marginBottom: '5px' },
  navContainer: {
    paddingLeft: '15px',
    color: '#1787e8',
    display: 'inline-flex' as const,
  },
  navTitle: { paddingLeft: '10px' },
  popoverContainer: { width: '310px' },
  navColumn: { marginBottom: '15px' },
  navWidth: { width: '100' },
} as const;

const getFilterValue = (name: string, multiple: boolean | undefined, data: Record<string, FilterData>): any => {
  if (name !== 'rede' && !multiple) {
    return data[name]?.value?.[0];
  }
  return data[name]?.value;
};

const FilterPicker = memo<FilterPickerProps>(({ isMultiple, filterConfig, data, value, onSelect, onClean, disabled = false }) => {
  const commonProps = {
    className: 'inf-picker',
    block: true,
    id: filterConfig.id,
    cleanable: filterConfig.cleanable,
    data: data[filterConfig.name]?.data || [],
    locale: filterConfig.locale,
    name: filterConfig.name,
    placeholder: filterConfig.placeholder,
    searchable: filterConfig.searchable,
    value,
    onSelect,
    onClean,
    disabled,
  };

  if (isMultiple) {
    return <CheckPicker {...commonProps} filter={filterConfig.searchable} />;
  }

  return <SelectPicker {...commonProps} />;
});

export const InputFilter = memo<InputFilterProps>(({ filter, index, handleDeleteFiltro, data, selectValue }) => {
  const handleSelect = useCallback(
    (name: string, value: any) => {
      selectValue(name, value);
    },
    [selectValue],
  );

  const handleClean = useCallback(
    (name: string, isMultiple: boolean | undefined) => {
      selectValue(name, isMultiple ? [] : '');
    },
    [selectValue],
  );

  const handleDelete = useCallback(() => {
    handleDeleteFiltro(index, filter.name);
  }, [handleDeleteFiltro, index, filter.name]);

  const mainFilterValue = getFilterValue(filter.name, filter.multiple, data);
  const childFilterValue = filter.child ? getFilterValue(filter.child.name, filter.child.multiple, data) : null;

  return (
    <FlexboxGrid.Item componentClass={Col} lg={filter.child ? 12 : 8} md={9} style={STYLES.marginBottom}>
      <FlexboxGrid.Item componentClass={Col} lg={filter.child ? 10 : 21} md={21} sm={23} xs={23} style={STYLES.marginBottom}>
        <FormGroup>
          <FilterPicker
            isMultiple={!!filter.multiple}
            filterConfig={filter}
            data={data}
            value={mainFilterValue}
            onSelect={(value: any) => handleSelect(filter.name, value)}
            onClean={() => handleClean(filter.name, filter.multiple)}
          />
        </FormGroup>
      </FlexboxGrid.Item>

      {filter.child &&
        (() => {
          const childFilter = filter.child;
          return (
            <FlexboxGrid.Item componentClass={Col} lg={11} md={7} sm={23} xs={23} style={STYLES.marginBottom}>
              <FormGroup>
                <FilterPicker
                  isMultiple={!!childFilter.multiple}
                  filterConfig={childFilter}
                  data={data}
                  value={childFilterValue}
                  onSelect={(value: any) => handleSelect(childFilter.name, value)}
                  onClean={() => handleClean(childFilter.name, childFilter.multiple)}
                  disabled={!data[filter.name]?.value}
                />
              </FormGroup>
            </FlexboxGrid.Item>
          );
        })()}

      <FlexboxGrid.Item componentClass={Col} lg={3} md={2} style={STYLES.marginBottom}>
        <IconButton icon={<Icon icon="trash2" as="svg" />} onClick={handleDelete} aria-label={`Remove ${filter.name} filter`} />
      </FlexboxGrid.Item>
    </FlexboxGrid.Item>
  );
});

const CustomNav = memo<CustomNavProps>(({ title, icon, itens, onSelect, ...props }) => (
  <Nav {...props} vertical onSelect={onSelect} style={STYLES.navWidth}>
    <div style={STYLES.navContainer}>
      <Icon icon={icon as any} />
      <p style={STYLES.navTitle}>{title}</p>
    </div>
    {itens?.map((item: FilterItem) => (
      <Nav.Item key={item.name} name={item.name} eventKey={item.name}>
        {item.title}
      </Nav.Item>
    ))}
  </Nav>
));

export const MoreFilterDropdown = memo<MoreFilterDropdownProps>(({ selectedFilters = [], filters = [], filtersType = [], addFilter, ...props }) => {
  const handleClick = useCallback(
    (_eventKey: any, event: any) => {
      const filterName = event.target.name;
      const isFilterAlreadySelected = selectedFilters.some((item: SelectedFilter) => item.name === filterName);

      if (!isFilterAlreadySelected) {
        const filterToAdd = filters.find((item: FilterConfig) => item.name === filterName);
        if (filterToAdd) {
          addFilter(filterToAdd);
        }
      }
    },
    [selectedFilters, filters, addFilter],
  );

  return (
    <Popover {...props}>
      <Row style={STYLES.popoverContainer}>
        {filtersType.map((item: FilterType) => (
          <Col md={12} key={item.title} style={STYLES.navColumn}>
            <CustomNav title={item.title} icon={item.icon} itens={item.itens} onSelect={handleClick} />
          </Col>
        ))}
      </Row>
    </Popover>
  );
});

FilterPicker.displayName = 'FilterPicker';
InputFilter.displayName = 'InputFilter';
CustomNav.displayName = 'CustomNav';
MoreFilterDropdown.displayName = 'MoreFilterDropdown';
